import React from 'react';
import { useTranslation } from 'react-i18next';
import BuyerCard from '../../ListingCard/BuyerCard';
import { ListingCardListing } from '../../ListingCard/types';
import { AreaDemandCard } from './CouldBuyFlow/areaInterestApi';
import {
  areasLabel,
  criteriaChips,
  criteriaPriceLabel,
  criteriaSizeLabel,
} from './CouldBuyFlow/areaInterestCard';
import { formatStreetLine } from '../../utils/addressFormatting';

interface DemandBuyerCardProps {
  card: AreaDemandCard;
  isLoggedIn?: boolean;
  setLoginModalVisible?: (visible: boolean) => void;
  /** Opens the interest's public single-listing view from clickable surfaces. */
  onOpen?: (listing: ListingCardListing) => void;
  /** Highlight this interest's area shape (and, for a circle interest, its
   *  anchor building) on the map while hovered (desktop). */
  onHover?: (card: AreaDemandCard) => void;
  onHoverEnd?: () => void;
}

/**
 * One area interest rendered as a buyer card (coin + looking-for size/rooms +
 * areas line + criteria chips + Share + Suggest). Area demand is a wider buyer
 * signal, so these interleave into the Buyers/All list, distance-sorted
 * alongside the per-building buyer cards. Both footer actions resolve off the
 * interest's own id: the share and tip endpoints look it up in either
 * collection, so a district-only interest with no anchor building works too.
 */
const DemandBuyerCard: React.FC<DemandBuyerCardProps> = ({
  card,
  isLoggedIn,
  setLoginModalVisible,
  onOpen,
  onHover,
  onHoverEnd,
}) => {
  const { t } = useTranslation();
  const sizeLabel = criteriaSizeLabel(card) || undefined;
  const anchorAddress = card.anchor_address
    ? formatStreetLine(card.anchor_address)
    : undefined;
  const listing: ListingCardListing = {
    _id: card.area_interest_id,
    // Marks the card as an area interest rather than a listing, which is what
    // useOpenListing tests to keep the translated criteria label out of the URL.
    area_interest_id: card.area_interest_id,
    building_id: card.anchor_building_id ?? undefined,
    // The viewer's own interest: BuyerCard withholds Suggest and shows the owner's hint.
    isUserOwnEvent: card.isUserOwnEvent,
    could_buy: true,
    could_sell: false,
    property_size: undefined,
    // rooms_min is a minimum stored as a free string ("3", "5+"); BuyerCard shows it as "N+ rooms".
    property_rooms: card.criteria.rooms_min as unknown as number,
    property_rooms_flexible: card.criteria.rooms_flexible,
    property_description: card.criteria.property_description,
    address: {
      street: areasLabel(card, t, anchorAddress || undefined),
      houseNumber: '',
      city: '',
    },
    updated_at: card.created_at,
  };
  return (
    <BuyerCard
      size="big"
      listing={listing}
      sizeLabel={sizeLabel}
      priceLabel={criteriaPriceLabel(card, true) || undefined}
      chips={criteriaChips(card, t)}
      isLoggedIn={isLoggedIn}
      setLoginModalVisible={setLoginModalVisible}
      onOpen={onOpen}
      onMouseEnter={onHover ? () => onHover(card) : undefined}
      onMouseLeave={onHoverEnd}
    />
  );
};

export default DemandBuyerCard;
